import type { TextSpacingModifierValue } from "schemas/generated/fileModifiers";

import React from "react";

import { useSemanticModifierValue } from "@agent-ui/features/modifiers/hooks/useSemanticModifierValue";
import { LengthInputSelector } from "@v2/features/modifiers/components/LengthInputSelector";
import { TEXT_SPACING_OPTIONS } from "@v2/features/modifiers/constants";
import { ChevronDown, LucideMoveHorizontal } from "lucide-react";
import { parseUnit } from "replo-runtime/shared/utils/units";

interface TextSpacingModifierProps {
  onChange: (modifiers: TextSpacingModifierValue[]) => void;
}

const TextSpacingModifier: React.FC<TextSpacingModifierProps> = ({
  onChange,
}) => {
  // NOTE (Derek, 2026-02-19): computedStyles.letterSpacing is in px (e.g. "0.8px").
  // We need fontSize to convert to em. If fontSize is "16px" and letterSpacing is "0.8px",
  // the em value is 0.8/16 = 0.05.
  const currentValue = useSemanticModifierValue({
    semanticExtractor: ({ computedStyles }) => {
      const letterSpacing = computedStyles.letterSpacing;
      if (!letterSpacing || letterSpacing === "normal") {
        return null;
      }
      const lsMatch = letterSpacing.match(/^(-?\d+(?:\.\d+)?)/);
      if (!lsMatch?.[1]) {
        return null;
      }
      const lsPx = Number.parseFloat(lsMatch[1]);
      if (isNaN(lsPx)) {
        return null;
      }
      const fontSize = computedStyles.fontSize;
      const fsMatch = fontSize?.match(/^(\d+(?:\.\d+)?)/);
      if (!fsMatch?.[1]) {
        return lsPx === 0 ? 0 : null;
      }
      const fsPx = Number.parseFloat(fsMatch[1]);
      if (isNaN(fsPx) || fsPx === 0) {
        return null;
      }
      // Convert px to em, round to 3 decimal places
      return Math.round((lsPx / fsPx) * 1000) / 1000;
    },
  });

  const displayValue = currentValue != null ? currentValue.toString() : null;

  const menuOptions = TEXT_SPACING_OPTIONS.map((option) => ({
    label: option.label,
    value: option.value.toString(),
  }));

  const handleChange = (newValue: string) => {
    const result = parseUnit(
      newValue,
      { value: 0, unit: "em" },
      "textSpacing",
      "em",
    );

    onChange([
      {
        type: "textSpacing" as const,
        value: Number.isNaN(Number(result.value)) ? 0 : Number(result.value),
      },
    ]);
  };

  return (
    <div className="text-spacing-modifier">
      <LengthInputSelector
        field="textSpacing"
        value={displayValue}
        startEnhancer={<LucideMoveHorizontal size={12} />}
        onChange={handleChange}
        placeholder="0em"
        metrics={["em"]}
        unitDefaults={{ value: "0", unit: "em" }}
        menuOptions={menuOptions}
        allowsNegativeValue
        minValues={{ em: -0.5 }}
        maxValues={{ em: 0.5 }}
        inputClassName="w-full"
        endEnhancer={() => <ChevronDown size={12} />}
      />
    </div>
  );
};

export default TextSpacingModifier;
